import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { useToast } from '../../shared/Toast';
import { StoreIngredient, createIngredient, deleteIngredient, listIngredients, updateIngredient } from './api';
import { useLibraryDraftGuard } from './draftGuard';
import {
  BTN, BTN_GHOST, BTN_PRIMARY, CHANGED, COLS_HEAD, CardSaveBar, EmptyState, FIELD, HINT, ICON_BTN, INVALID, LABEL,
  LibraryPane, LoadState, MoneyInput, OPEN_ROW, SearchField, Toggle, UsageCount, euro, searchKey, similarName,
  useCatalogLock, useLibraryErrorText,
} from './parts';

/**
 * Κατάλογος → «Υλικά» (mock Φάσης 3, 12/10/2026): για «χωρίς» και έξτρα στο ταμείο. Η τιμή εδώ είναι η
 * προεπιλογή — κάθε προϊόν μπορεί να έχει δική του. Η κατηγορία υλικού είναι ελεύθερο κείμενο
 * (store_ingredients.category) και γίνεται φίλτρο.
 * «Διαθέσιμο» στη γραμμή αλλάζει ΑΜΕΣΩΣ, όπως πριν (τελείωσε η φέτα μέσα στη βάρδια)· όλα τα άλλα με
 * την «Αποθήκευση» της ανοιχτής γραμμής (hooks/useDirtyForm, guardId).
 * Ο server ξαναγράφει όλη τη γραμμή σε κάθε PUT — στέλνουμε πάντα και τη σειρά της (display_order).
 */

interface IngredientDraft { name: string; category: string; price: number | null; is_available: boolean }

const EMPTY_DRAFT: IngredientDraft = { name: '', category: '', price: 0, is_available: true };

const draftOf = (i: StoreIngredient): IngredientDraft => ({
  name: i.name, category: i.category || '', price: i.extra_price, is_available: i.is_available,
});

type Open = number | 'new' | null;
/** Φίλτρο κατηγορίας: όλα, μία, ή «χωρίς κατηγορία». */
type Filter = { kind: 'all' } | { kind: 'none' } | { kind: 'cat'; name: string };

const GRID = 'grid grid-cols-[minmax(0,1fr)_88px_44px_32px] md:grid-cols-[minmax(160px,1.3fr)_minmax(120px,1fr)_104px_96px_80px_32px] items-center gap-x-3';

const IngredientsTab: React.FC = () => {
  const { t } = useTranslation(['products', 'common', 'settings']);
  const { store } = useAuth();
  const storeId = store?.id;
  const { showToast } = useToast();
  const errorText = useLibraryErrorText();
  const { locked, noteError } = useCatalogLock();
  const form = useDirtyForm<IngredientDraft>(EMPTY_DRAFT, { guardId: 'catalog:ingredients' });
  useLibraryDraftGuard('ingredients', form.dirty);
  const { values: v, saved } = form;

  const [items, setItems] = useState<StoreIngredient[]>([]);
  const [load, setLoad] = useState<'loading' | 'ready' | 'error'>('loading');
  const [open, setOpen] = useState<Open>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>({ kind: 'all' });
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState<number | null>(null);
  const [cardError, setCardError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const ids = { name: useId(), category: useId(), price: useId(), available: useId(), list: useId() };
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const loadItems = useCallback(async (): Promise<StoreIngredient[] | null> => {
    if (!storeId) return null;
    try {
      const list = await listIngredients(storeId);
      if (!alive.current) return null;
      setItems(list);
      setLoad('ready');
      return list;
    } catch (err) {
      console.error('Ingredients load failed:', err);
      if (alive.current) setLoad(prev => (prev === 'ready' ? prev : 'error'));
      return null;
    }
  }, [storeId]);

  useEffect(() => {
    setOpen(null);
    form.reset(EMPTY_DRAFT);
    setLoad('loading');
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- μόνο σε αλλαγή καταστήματος
  }, [storeId]);

  // Κλείδωσε (κεντρικός κατάλογος, π.χ. 403 από τον server): το πρόχειρο δεν θα αποθηκευτεί ποτέ.
  useEffect(() => {
    if (!locked || open === null) return;
    setOpen(null);
    form.reset(EMPTY_DRAFT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, open]);

  const openItem = items.find(i => i.id === open) || null;

  const confirmDropDraft = (): boolean => {
    if (!form.dirty) return true;
    if (open === 'new') return window.confirm(t('products:libraries.discardNew'));
    const name = (openItem?.name || v.name).trim() || t('products:libraries.unnamed');
    return window.confirm(t('products:libraries.discard', { name }));
  };

  const switchTo = (next: Open): boolean => {
    if (!confirmDropDraft()) return false;
    setCardError(null);
    setShowErrors(false);
    setOpen(next);
    if (next === null || next === 'new') form.reset(EMPTY_DRAFT);
    else {
      const it = items.find(x => x.id === next);
      if (it) form.reset(draftOf(it));
    }
    return true;
  };

  /* ───────────── έλεγχος ───────────── */

  const nameError = (() => {
    const name = v.name.trim();
    if (!name) return t('products:libraries.validation.nameRequired');
    const renamed = open === 'new' || name !== saved.name.trim();
    if (renamed && items.some(i => i.id !== open && similarName(i.name, name))) return t('products:libraries.validation.nameTaken', { name });
    return null;
  })();
  const priceError = (() => {
    if (v.price == null) return t('products:libraries.validation.priceInvalid');
    if (v.price < 0 && (open === 'new' || v.price !== saved.price)) return t('products:libraries.validation.priceNegative');
    return null;
  })();
  const valid = !nameError && !priceError;

  /* ───────────── αποθήκευση ───────────── */

  const save = async () => {
    if (saving || locked || !storeId) return;
    if (!valid) { setShowErrors(true); return; }
    setSaving(true);
    setCardError(null);
    const body = {
      name: v.name.trim(),
      extra_price: v.price as number,
      is_available: v.is_available,
      category: v.category.trim() || null,
    };
    try {
      let id: number;
      if (open === 'new') {
        id = (await createIngredient(storeId, { name: body.name, price: body.extra_price, category: body.category, is_available: body.is_available })).id;
      } else if (openItem) {
        id = openItem.id;
        await updateIngredient(id, { ...body, display_order: openItem.display_order });
      } else {
        return;
      }
      const fresh = await loadItems();
      if (!alive.current) return;
      const row = fresh?.find(x => x.id === id);
      if (row) { setOpen(row.id); form.reset(draftOf(row)); } else { setOpen(null); form.reset(EMPTY_DRAFT); }
      showToast('success', t(open === 'new' ? 'products:libraries.createdToast' : 'products:libraries.savedToast', { name: body.name }));
    } catch (err) {
      noteError(err);
      if (alive.current) setCardError(errorText(err));
    } finally {
      if (alive.current) { setSaving(false); setShowErrors(false); }
    }
  };

  const reset = () => {
    setCardError(null);
    setShowErrors(false);
    if (open === 'new') {
      if (form.dirty && !window.confirm(t('products:libraries.discardNew'))) return;
      setOpen(null);
      form.reset(EMPTY_DRAFT);
      return;
    }
    form.reset();
  };

  /** Ο διακόπτης της γραμμής: αμέσως, χωρίς πρόχειρο. */
  const toggleAvailable = async (it: StoreIngredient) => {
    if (locked || toggling != null) return;
    setToggling(it.id);
    setListError(null);
    const next = !it.is_available;
    try {
      await updateIngredient(it.id, {
        name: it.name, extra_price: it.extra_price, is_available: next, category: it.category, display_order: it.display_order,
      });
      if (!alive.current) return;
      setItems(list => list.map(x => (x.id === it.id ? { ...x, is_available: next } : x)));
      showToast('success', t(next ? 'products:libraries.ingredients.availabilityOn' : 'products:libraries.ingredients.availabilityOff', { name: it.name }));
    } catch (err) {
      noteError(err);
      if (alive.current) setListError(t('products:libraries.errors.actionFailed', { reason: errorText(err) }));
    } finally {
      if (alive.current) setToggling(null);
    }
  };

  const remove = async (it: StoreIngredient) => {
    const message = it.product_count
      ? t('products:libraries.ingredients.deleteConfirmUsed', { name: it.name, count: it.product_count })
      : t('products:libraries.ingredients.deleteConfirm', { name: it.name });
    if (!window.confirm(message)) return;
    try {
      await deleteIngredient(it.id);
    } catch (err) {
      noteError(err);
      setCardError(t('products:libraries.errors.actionFailed', { reason: errorText(err) }));
      return;
    }
    setOpen(null);
    form.reset(EMPTY_DRAFT);
    await loadItems();
    if (alive.current) showToast('success', t('products:libraries.deletedToast', { name: it.name }));
  };

  /* ───────────── εμφάνιση ───────────── */

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    let none = 0;
    items.forEach(i => {
      if (i.category) counts.set(i.category, (counts.get(i.category) || 0) + 1);
      else none += 1;
    });
    const named = Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0], 'el'));
    return { named, none };
  }, [items]);

  // Φίλτρο που άδειασε (σβήστηκε η τελευταία «Σάλτσα») → πίσω στα «Όλα».
  useEffect(() => {
    if (filter.kind === 'cat' && !categories.named.some(([name]) => name === filter.name)) setFilter({ kind: 'all' });
    if (filter.kind === 'none' && categories.none === 0) setFilter({ kind: 'all' });
  }, [categories, filter]);

  const visible = useMemo(() => {
    const q = searchKey(search.trim());
    return items.filter(i => {
      if (i.id === open) return true;
      if (filter.kind === 'none' && i.category) return false;
      if (filter.kind === 'cat' && i.category !== filter.name) return false;
      return !q || searchKey(i.name).includes(q) || searchKey(i.category || '').includes(q);
    });
  }, [items, search, filter, open]);

  const chip = (key: string, label: string, count: number, on: boolean, select: () => void) => (
    <button
      key={key}
      type="button"
      aria-pressed={on}
      onClick={select}
      className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[12.5px] transition-colors ${
        on ? 'border-brand-line bg-brand-soft font-semibold text-brand' : 'border-line bg-surface-2 font-medium text-t2 hover:bg-surface-3/60'
      }`}
    >
      {label}
      <span className={`tabular-nums ${on ? 'text-brand/75' : 'text-t4'}`}>{count}</span>
    </button>
  );

  const priceText = (n: number) => (n ? `+${euro(n)}` : '—');

  const editor = (it: StoreIngredient | null) => {
    const isNew = it === null;
    return (
      <div className="flex flex-col gap-3.5 px-[18px] pb-4 pt-1 md:pl-[30px]">
        <div className="grid grid-cols-1 gap-x-5 gap-y-3.5 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,150px)] max-w-4xl">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={ids.name} className={LABEL}>{t('products:libraries.ingredients.nameLabel')}</label>
            <input
              id={ids.name}
              type="text"
              value={v.name}
              maxLength={255}
              autoFocus
              placeholder={t('products:libraries.ingredients.namePlaceholder')}
              onChange={e => form.setField('name', e.target.value)}
              aria-invalid={showErrors && nameError ? true : undefined}
              className={`${FIELD} ${!isNew && v.name !== saved.name ? CHANGED : ''} ${showErrors && nameError ? INVALID : ''}`}
            />
            {showErrors && nameError && <p className="text-xs text-red-700 dark:text-red-300">{nameError}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={ids.category} className={LABEL}>
              {t('products:libraries.ingredients.categoryLabel')}
              <span className="ml-1 font-normal text-t4">{t('products:libraries.optional')}</span>
            </label>
            <input
              id={ids.category}
              type="text"
              list={ids.list}
              value={v.category}
              maxLength={100}
              placeholder={t('products:libraries.ingredients.categoryPlaceholder')}
              onChange={e => form.setField('category', e.target.value)}
              className={`${FIELD} ${!isNew && v.category !== saved.category ? CHANGED : ''}`}
            />
            <datalist id={ids.list}>
              {categories.named.map(([name]) => <option key={name} value={name} />)}
            </datalist>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={ids.price} className={LABEL}>{t('products:libraries.ingredients.priceLabel')}</label>
            <MoneyInput
              id={ids.price}
              size="md"
              value={v.price}
              onChange={price => form.setField('price', price)}
              label={t('products:libraries.ingredients.priceLabel')}
              changed={!isNew && v.price !== saved.price}
              invalid={showErrors && !!priceError}
            />
            {showErrors && priceError && <p className="text-xs text-red-700 dark:text-red-300">{priceError}</p>}
          </div>
        </div>
        <p className={`${HINT} -mt-1.5`}>{t('products:libraries.ingredients.priceHint')}</p>
        <div className="flex items-start gap-2.5">
          <span className="pt-px">
            <Toggle
              checked={v.is_available}
              onChange={next => form.setField('is_available', next)}
              labelledBy={ids.available}
              changed={!isNew && v.is_available !== saved.is_available}
            />
          </span>
          <span>
            <b id={ids.available} className="block text-[13.5px] font-semibold text-t1">{t('products:libraries.ingredients.availableLabel')}</b>
            <span className={`block ${HINT}`}>{t('products:libraries.ingredients.availableHint')}</span>
          </span>
        </div>
        <CardSaveBar
          dirty={form.dirty}
          saving={saving}
          canSave={!showErrors || valid}
          onSave={save}
          onReset={reset}
          isNew={isNew}
          onClose={() => switchTo(null)}
          error={cardError}
          leading={!isNew && it && (
            <button type="button" onClick={() => remove(it)} className={`${BTN_GHOST} !text-red-700 dark:!text-red-300`}>
              <Trash2 className="h-[15px] w-[15px]" aria-hidden="true" />
              {t('products:libraries.ingredients.deleteIngredient')}
            </button>
          )}
        />
      </div>
    );
  };

  const row = (it: StoreIngredient) => {
    const isOpen = open === it.id && !locked;
    return (
      <div key={it.id} data-testid={`ingredient-${it.id}`} className={`border-b border-line ${isOpen ? OPEN_ROW : ''}`}>
        <div className={`${GRID} min-h-[44px] py-1 pl-[18px] pr-3.5 text-[13.5px]`}>
          <span className="flex min-w-0 items-center gap-2">
            <b className={`truncate font-semibold ${it.is_available ? 'text-t1' : 'text-t3'}`}>{it.name}</b>
            {isOpen && form.dirty && (
              <span
                role="img"
                aria-label={t('products:libraries.changedMark')}
                title={t('products:libraries.changedMark')}
                className="h-[7px] w-[7px] shrink-0 rounded-full bg-amber-500 shadow-[0_0_0_3px_rgb(245_158_11_/_22%)]"
              />
            )}
          </span>
          <span className="hidden min-w-0 md:block">
            {it.category
              ? <span className="inline-flex h-5 max-w-full items-center truncate rounded-full bg-surface-3/80 px-[7px] text-[11.5px] font-medium text-t2">{it.category}</span>
              : <span className="text-t4">—</span>}
          </span>
          <span className="text-right tabular-nums text-t1" title={it.extra_price ? undefined : t('products:libraries.ingredients.noCharge')}>
            {priceText(it.extra_price)}
          </span>
          <span className="hidden text-right md:block"><UsageCount count={it.product_count} /></span>
          <span className="grid place-items-center">
            <Toggle
              checked={it.is_available}
              onChange={locked || isOpen ? undefined : () => toggleAvailable(it)}
              disabled={locked || isOpen || toggling === it.id}
              label={t('products:libraries.ingredients.availableAria', { name: it.name })}
            />
          </span>
          <span className="flex justify-end">
            {!locked && (
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => switchTo(isOpen ? null : it.id)}
                aria-label={t('products:libraries.edit', { name: it.name })}
                title={t('products:libraries.edit', { name: it.name })}
                className={`${ICON_BTN} ${isOpen ? 'bg-brand-soft text-brand' : ''}`}
              >
                <Pencil className="h-[15px] w-[15px]" aria-hidden="true" />
              </button>
            )}
          </span>
        </div>
        {isOpen && editor(it)}
      </div>
    );
  };

  return (
    <LibraryPane
      testId="ingredients-tab"
      title={t('products:libraries.ingredients.title')}
      count={load === 'ready' ? items.length : null}
      hint={t('products:libraries.ingredients.hint')}
      locked={locked}
      actions={(
        <>
          <SearchField value={search} onChange={setSearch} placeholder={t('products:libraries.ingredients.search')} />
          {!locked && (
            <button type="button" onClick={() => switchTo('new')} disabled={load !== 'ready' || open === 'new'} className={BTN_PRIMARY}>
              <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
              {t('products:libraries.ingredients.new')}
            </button>
          )}
        </>
      )}
    >
      {listError && (
        <p role="alert" className="mx-[18px] mt-3 rounded-[10px] border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-800 dark:text-red-200">
          {listError}
        </p>
      )}
      {load !== 'ready' ? (
        <LoadState state={load} onRetry={() => { setLoad('loading'); loadItems(); }} />
      ) : (
        <>
          {items.length > 0 && (
            <div role="group" aria-label={t('products:libraries.ingredients.filterAria')} className="flex flex-wrap gap-1.5 border-b border-line px-[18px] py-2.5">
              {chip('all', t('products:libraries.ingredients.all'), items.length, filter.kind === 'all', () => setFilter({ kind: 'all' }))}
              {categories.named.map(([name, count]) =>
                chip(`cat-${name}`, name, count, filter.kind === 'cat' && filter.name === name, () => setFilter({ kind: 'cat', name })))}
              {categories.none > 0 && chip('none', t('products:libraries.ingredients.uncategorized'), categories.none, filter.kind === 'none', () => setFilter({ kind: 'none' }))}
            </div>
          )}
          <div className={`${COLS_HEAD} ${GRID} py-2 pl-[18px] pr-3.5`}>
            <span>{t('products:libraries.ingredients.colName')}</span>
            <span className="hidden md:block">{t('products:libraries.ingredients.colCategory')}</span>
            <span className="text-right">{t('products:libraries.ingredients.colPrice')}</span>
            <span className="hidden text-right md:block">{t('products:libraries.usedIn')}</span>
            <span className="text-center">{t('products:libraries.ingredients.colAvailable')}</span>
            <span />
          </div>
          <div className="flex-1 min-h-0 overflow-auto">
            {open === 'new' && (
              <div data-testid="ingredient-new" className={`border-b border-line ${OPEN_ROW}`}>
                <div className="flex min-h-[44px] flex-wrap items-center gap-x-2.5 py-2 pl-[18px] pr-3.5">
                  <Plus className="h-4 w-4 text-brand" aria-hidden="true" />
                  <b className="text-sm font-semibold text-t1">{t('products:libraries.ingredients.newTitle')}</b>
                </div>
                {editor(null)}
              </div>
            )}
            {items.length === 0 && open !== 'new' ? (
              <EmptyState
                title={t('products:libraries.ingredients.empty')}
                hint={t('products:libraries.ingredients.emptyHint')}
                action={!locked && (
                  <button type="button" onClick={() => switchTo('new')} className={BTN}>
                    <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
                    {t('products:libraries.ingredients.new')}
                  </button>
                )}
              />
            ) : visible.length === 0 && open !== 'new' ? (
              <EmptyState title={t('products:libraries.noMatches', { query: search.trim() })} />
            ) : (
              visible.map(row)
            )}
            {visible.length > 0 && visible.length < items.length && (
              <p className="px-[18px] py-3.5 text-[12.5px] text-t4">
                {t('products:libraries.ingredients.shown', { shown: visible.length, total: items.length })}
              </p>
            )}
          </div>
        </>
      )}
    </LibraryPane>
  );
};

export default IngredientsTab;
